import { IChatConversation } from '@dataTypes/chat.type'
import { ICalls } from '@dataTypes/calls.type'
import { getFullName } from './fullname.util'
import { Patient } from '@dataTypes/PatientInfo.types'
import { DateTime } from 'luxon'

type CallOutcomeInput = Pick<
  ICalls,
  | 'call_type'
  | 'call_status'
  | 'answered_at'
  | 'answer_tracking_version'
  | 'end_date_time'
>
export const getCallDirection = (
  call: Pick<ICalls, 'call_type'>,
): 'Incoming' | 'Outgoing' =>
  ['Outgoing', 'Decline', 'Declined'].includes(call.call_type)
    ? 'Outgoing'
    : 'Incoming'

export const getCallAnswerState = (call: CallOutcomeInput): boolean | null => {
  if (call.answered_at) return true
  if (call.answer_tracking_version === 1) return false
  if (
    ['Missed', 'Decline', 'Declined'].includes(call.call_type) ||
    ['No Answer', 'Busy', 'Failed', 'Cancelled'].includes(call.call_status)
  )
    return false
  return null // Legacy Completed/duration never proves both parties joined.
}
export const getCallDisplayType = (
  call: CallOutcomeInput,
): 'Incoming' | 'Outgoing' | 'Missed' | 'Decline' => {
  const direction = getCallDirection(call)
  const terminal =
    ['Completed', 'No Answer', 'Busy', 'Failed', 'Cancelled'].includes(
      call.call_status,
    ) || !!call.end_date_time
  if (getCallAnswerState(call) === false && terminal)
    return direction === 'Incoming' ? 'Missed' : 'Decline'
  // Retain explicit legacy outcomes even where old records lack a terminal status.
  if (
    call.answer_tracking_version !== 1 &&
    ['Missed', 'Decline'].includes(call.call_type)
  )
    return call.call_type as 'Missed' | 'Decline'
  return direction
}
export const getCallDisplayTooltip = (call: CallOutcomeInput): string => {
  const type = getCallDisplayType(call)
  const label = type === 'Decline' ? 'Declined' : type
  return label + ' call'
}

export const MAX_CALL_AI_RETRIES = 3
const MIN_CALL_AI_RECORDING_SECONDS = 3

export const isCallAiExhausted = (call: ICalls): boolean =>
  call.call_ai_job?.status === 'failed' &&
  (call.call_ai_job.retry_count ?? 0) >= MAX_CALL_AI_RETRIES

export const isCallAiInProgress = (call: ICalls): boolean => {
  const job = call.call_ai_job
  if (job) {
    if (job.status === 'completed') return false
    if (job.status === 'failed') {
      return (job.retry_count ?? 0) < MAX_CALL_AI_RETRIES
    }
    return job.status === 'pending' || job.status === 'processing'
  }

  const hasAiOutput =
    Boolean(call.summary?.trim()) ||
    Boolean(call.sentiments?.trim()) ||
    (call.tags?.length ?? 0) > 0
  if (hasAiOutput) return false
  if (!call.recording_url) return false
  if (!['Incoming', 'Outgoing'].includes(call.call_type)) return false
  if ((call.duration ?? 0) < MIN_CALL_AI_RECORDING_SECONDS) return false
  return true
}

export const getCallAiPlaceholder = (call: ICalls): string =>
  isCallAiInProgress(call) ? 'Analyzing...' : 'N/A'

export const getDisplayName = (
  patients: [] | Patient[],
  conversation: IChatConversation[] | null,
  cellphone: string,
): string => {
  if (cellphone.length === 0) return 'N/A'
  let patientName = getFullName(
    patients.find((el) => el.cellphone === cellphone) ?? null,
  )
  if (patientName === 'N/A')
    patientName = getFullName(
      conversation?.find((el) => {
        if (el.chat_participant && el.chat_participant.length !== 0)
          return el.chat_participant[0].phone_number === cellphone
      })?.chat_participant[0].contact ?? null,
    )

  return patientName === 'N/A' ? cellphone : patientName
}

// API timestamps are instants. Legacy offset-free call timestamps represent UTC.
const parseCallDate = (date?: string | Date | null): DateTime | null => {
  if (!date) return null
  const parsed =
    date instanceof Date
      ? DateTime.fromJSDate(date, { zone: 'utc' })
      : DateTime.fromISO(date.trim().replace(' ', 'T'), {
          zone: 'utc',
          setZone: true,
        })
  return parsed.isValid ? parsed : null
}

export const getCallTimestamp = (date?: string | Date | null): number | null =>
  parseCallDate(date)?.toMillis() ?? null

export const formatCallDate = (
  date?: string | Date | null,
  timeZone = 'UTC',
  now = new Date(),
): string => {
  const instant = parseCallDate(date)
  if (!instant) return 'N/A'
  const zone = DateTime.fromJSDate(now).setZone(timeZone).isValid
    ? timeZone
    : 'UTC'
  const callDate = instant.setZone(zone).setLocale('en-US')
  const today = DateTime.fromJSDate(now, { zone })
  const time = callDate.toFormat('hh:mm a')
  if (callDate.hasSame(today, 'day')) return `Today, ${time}`
  if (callDate.hasSame(today.minus({ days: 1 }), 'day'))
    return `Yesterday, ${time}`
  return `${callDate.toFormat('MM/dd/yyyy')} - ${time}`
}
